import React, {useRef, useEffect, useState} from 'react';
import SwiperCore, {A11y, Autoplay, Navigation, Pagination, Scrollbar} from 'swiper';
import {Swiper, SwiperSlide} from 'swiper/react';
import 'swiper/swiper-bundle.min.css';
import BusinessService from '../../services/business.service';
import GalleryService from '../../services/gallery.service';
import {useRouter} from 'next/router';
SwiperCore.use([Navigation, Pagination, Scrollbar, A11y, Autoplay]);
import {getLocaleContent} from '@helper/locale-content.js';
import styles from './GalleryService.module.css';
import {changeToSlug} from '@helper/string.helper';

export default function SlideHome() {
    const {locale} = useRouter();
    const [businessData, setBusinessData] = useState([]);
    const [activeIndex, setActiveIndex] = useState(0);
    const [galleryData, setGalleryData] = useState([]);
    const titleRef = useRef(null);

    useEffect(() => {
        const businessService = new BusinessService();
        businessService
            .getBusinessHome({locale})
            .then(response => {
                setBusinessData(Array.isArray(response) ? response : []);
                if (response?.length) fetchGalleryData(response[0].business_id);
            })
            .catch(err => console.error('Error fetching business data:', err));
    }, [locale]);

    const fetchGalleryData = businessId => {
        if (!businessId) return;
        const galleryService = new GalleryService();
        galleryService
            .getGalleryHome({business_id: businessId, locale: locale})
            .then(res => {
                if (Array.isArray(res)) {
                    const sortedData = [...res].sort((a, b) => {
                        return (b.order_index ?? 0) - (a.order_index ?? 0);
                    });
                    console.log(sortedData);
                    setGalleryData(sortedData);
                } else {
                    setGalleryData([]);
                }
            })
            .catch(err => console.error('Error fetching gallery data:', err));
    };

    const handleButtonClick = index => {
        setActiveIndex(index);
        const b = businessData[index];
        if (b) fetchGalleryData(b.business_id);
    };

    const handleScroll = e => {
        const container = e.currentTarget;
        const text = container.querySelector(`.${styles['overlay-text']}`);
        if (!text) return;

        const visibleHeight = container.clientHeight; // chính xác vùng nhìn thấy
        const textHeight = text.scrollHeight;

        text.style.transition = 'none';
        text.style.transform = 'translateY(0)';

        if (textHeight <= visibleHeight) {
            container.style.alignItems = 'center';
            return;
        }

        container.style.alignItems = 'flex-start';

        // 👇 scroll hết nội dung
        const distance = -(textHeight - visibleHeight) - 20;
        const duration = (textHeight - visibleHeight) / 30; // 30px / giây

        container._scrollTimeout = setTimeout(() => {
            text.style.transition = `transform ${duration}s linear`;
            text.style.transform = `translateY(${distance}px)`;
        }, 1000);
    };

    const resetScroll = e => {
        const container = e.currentTarget;
        const text = container.querySelector(`.${styles['overlay-text']}`);
        if (!text) return;

        if (container._scrollTimeout) {
            clearTimeout(container._scrollTimeout);
        }

        text.style.transition = 'none';
        text.style.transform = 'translateY(0)';

        // reset về center khi rời hover
        container.style.alignItems = 'center';
    };

    return (
        <div className={styles['gallery-wrapper']}>
            <section className={`${styles['fac']} container`} aria-labelledby="fac-title">
                <header className="fac__head">
                    <h2 id="fac-title" className="fac__title">
                        {getLocaleContent(locale, 'page.gallery.title1')}{' '}
                        <span>{getLocaleContent(locale, 'page.gallery.title2')}</span>{' '}
                        {getLocaleContent(locale, 'page.gallery.title3')}
                    </h2>

                    {/* Use Swiper for horizontal scroll of pills */}
                    <Swiper
                        className={styles['fac__tabs-swiper']}
                        slidesPerView="auto"
                        breakpoints={{
                            0: {
                                spaceBetween: 10,
                            },
                            1024: {
                                spaceBetween: 24,
                            },
                        }}
                        freeMode={true}>
                        {businessData.map((business, index) => (
                            <SwiperSlide key={business.business_id || index} className={styles['tab-slide']}>
                                <button
                                    type="button"
                                    className={`${styles['pill']} ${index === activeIndex ? styles['is-active'] : ''}`}
                                    onClick={() => handleButtonClick(index)}
                                    aria-pressed={index === activeIndex}>
                                    {business.business_name || '—'}
                                </button>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </header>

                <div className="fac__gallery">
                    <aside className="fac__panel">
                        <p className="fac__eyebrow">{getLocaleContent(locale, 'page.gallery.explore-title')}</p>
                        <h3 className="fac__panel-title">{businessData[activeIndex]?.business_name || ''}</h3>
                        <a
                            href={`${changeToSlug(businessData[activeIndex]?.business_name)}-m${
                                businessData[activeIndex]?.business_id
                            }`}
                            className="fac__cta">
                            {getLocaleContent(locale, 'page.gallery.panel-title')}
                            <svg
                                viewBox="0 0 30 30"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                className="arrow-svg">
                                <circle className="arrow-circle" cx="15" cy="15" r="15" fill="#006940" />
                                <path
                                    className="arrow-path"
                                    d="M18.9375 12.1875L21.75 15M21.75 15L18.9375 17.8125M21.75 15H8.25"
                                    stroke="white"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </a>
                    </aside>

                    <figure className="g g-b">
                        <div className={styles['img-wrap']}>
                            <img src={galleryData[0]?.image_url || ''} alt={galleryData[0]?.description} />
                            <div
                                className={styles['img-overlay']}
                                onMouseEnter={e => handleScroll(e)}
                                onMouseLeave={e => resetScroll(e)}>
                                <div className={styles['overlay-text']}>
                                    <span>{galleryData[0]?.description}</span>
                                </div>
                            </div>
                        </div>
                    </figure>
                    <figure className="g g-cg">
                        <div className={styles['img-wrap']}>
                            <img src={galleryData[1]?.image_url || ''} alt={galleryData[1]?.description} />
                            <div className={styles['img-overlay']}>
                                <span>{galleryData[1]?.description}</span>
                            </div>
                        </div>
                    </figure>
                    <figure className="g g-d">
                        <div className={styles['img-wrap']}>
                            <img src={galleryData[2]?.image_url || ''} alt={galleryData[2]?.description} />
                            <div className={styles['img-overlay']}>
                                <span>{galleryData[2]?.description}</span>
                            </div>
                        </div>
                    </figure>
                    <figure className="g g-e">
                        <div className={styles['img-wrap']}>
                            <img src={galleryData[3]?.image_url || ''} alt={galleryData[3]?.description} />
                            <div className={styles['img-overlay']}>
                                <span>{galleryData[3]?.description}</span>
                            </div>
                        </div>
                    </figure>
                    <figure className="g g-f">
                        <div className={styles['img-wrap']}>
                            <img src={galleryData[4]?.image_url || ''} alt={galleryData[4]?.description} />
                            <div className={styles['img-overlay']}>
                                <span>{galleryData[4]?.description}</span>
                            </div>
                        </div>
                    </figure>
                    <figure className="g g-i">
                        <div className={styles['img-wrap']}>
                            <img src={galleryData[5]?.image_url || ''} alt={galleryData[5]?.description} />
                            <div className={styles['img-overlay']}>
                                <span>{galleryData[5]?.description}</span>
                            </div>
                        </div>
                    </figure>
                    <figure className="g g-h">
                        <div className={styles['img-wrap']}>
                            <img src={galleryData[6]?.image_url || ''} alt={galleryData[6]?.description} />
                            <div className={styles['img-overlay']}>
                                <span>{galleryData[6]?.description}</span>
                            </div>
                        </div>
                    </figure>
                    <figure className="g g-j">
                        <div className={styles['img-wrap']}>
                            <img src={galleryData[7]?.image_url || ''} alt={galleryData[7]?.description} />
                            <div className={styles['img-overlay']}>
                                <span>{galleryData[7]?.description}</span>
                            </div>
                        </div>
                    </figure>
                    <figure className="g g-k">
                        <div className={styles['img-wrap']}>
                            <img src={galleryData[8]?.image_url || ''} alt={galleryData[8]?.description} />
                            <div className={styles['img-overlay']}>
                                <span>{galleryData[8]?.description}</span>
                            </div>
                        </div>
                    </figure>
                </div>
            </section>
        </div>
    );
}
